/**
 * Pure, unit-tested logic behind the offers Kanban board: stage columns (position order, archived hidden),
 * per-column amount sum, in-column sort, and the cross-view search
 * predicate. Kept out of OffersBoardPage.tsx so it's testable without
 * React/DOM (mirrors the offersFilter.ts idiom).
 *
 * Client-side only, same as offersFilter.ts: the full offers list is already
 * loaded by the page.
 */
import type { Offer } from "../api/offers";
import type { OfferStage } from "../api/offerStages";

/** Slice of OfferStage the board's pure functions need. */
export type BoardStage = Pick<OfferStage, "id" | "position" | "archived">;

/** Slice of Offer the board's pure functions need. */
export type BoardOffer = Pick<Offer, "id" | "customerName" | "offerNumber" | "displayAmount" | "dueAt" | "updatedAt"> & {
  stage: { id: string } | null;
  items: Pick<Offer["items"][number], "name">[];
};

export type BoardSort = "due" | "modified";

/** Active (non-archived) stages, sorted by position ascending — the board's column order. */
export function activeStages<T extends BoardStage>(stages: T[]): T[] {
  return [...stages].filter((s) => !s.archived).sort((a, b) => a.position - b.position);
}

/** Case-insensitive substring match over customer name, offer number, and item product names. */
export function matchesSearch<T extends BoardOffer>(offer: T, query: string): boolean {
  const q = query.trim().toLowerCase();
  if (!q) return true;
  if (offer.customerName?.toLowerCase().includes(q)) return true;
  if (offer.offerNumber?.toLowerCase().includes(q)) return true;
  return offer.items.some((it) => (it.name ?? "").toLowerCase().includes(q));
}

/** Filters offers by the shared search box — applied identically on board/list/funnel. */
export function searchOffers<T extends BoardOffer>(offers: T[], query: string): T[] {
  return offers.filter((o) => matchesSearch(o, query));
}

/** Sum of displayAmount over a set of offers; null/undefined amounts count as 0. */
export function sumDisplayAmount<T extends Pick<Offer, "displayAmount">>(offers: T[]): number {
  return offers.reduce((sum, o) => sum + (o.displayAmount ?? 0), 0);
}

/**
 * In-column sort. "due": ascending by dueAt so the most urgent/overdue offer
 * floats to the top; offers with no due date sort last. "modified":
 * descending by updatedAt, most recently touched first.
 */
export function sortOffers<T extends BoardOffer>(offers: T[], sort: BoardSort): T[] {
  const copy = [...offers];
  copy.sort((a, b) => {
    if (sort === "due") {
      if (a.dueAt == null && b.dueAt == null) return 0;
      if (a.dueAt == null) return 1;
      if (b.dueAt == null) return -1;
      return a.dueAt < b.dueAt ? -1 : a.dueAt > b.dueAt ? 1 : 0;
    }
    return a.updatedAt < b.updatedAt ? 1 : a.updatedAt > b.updatedAt ? -1 : 0;
  });
  return copy;
}

export type BoardColumn<S, O> = { stage: S; offers: O[]; sumAmount: number };

/**
 * Groups offers into one column per active stage (position order), applying
 * the in-column sort and per-column amount sum. Offers whose stage isn't in
 * the active set (archived stage, or — defensively — no stage at all) are
 * dropped from every column, the same "archived hidden" rule the columns
 * themselves follow.
 */
export function groupByStage<S extends BoardStage, O extends BoardOffer>(
  offers: O[],
  stages: S[],
  sort: BoardSort,
): BoardColumn<S, O>[] {
  return activeStages(stages).map((stage) => {
    const inStage = sortOffers(
      offers.filter((o) => o.stage?.id === stage.id),
      sort,
    );
    return { stage, offers: inStage, sumAmount: sumDisplayAmount(inStage) };
  });
}

// ---- Card presentation helpers -----------------------------------------
// Shared by OffersBoardPage (columns + cards), OffersPage (stage pill in the
// list table) and OfferEditorPage (stage badge) so all three render the same
// stage color and card-subtitle logic instead of drifting apart.

/** Neutral fallback for a stage with no admin-picked color and no kind/position hint. */
export const DEFAULT_STAGE_COLOR = "var(--ink-3)";

// Distinct per-position hues for OPEN stages that carry no admin-picked color,
// so columns/cards never all render the same grey. Light->dark blue ordinal
// ramp (the sequential steps used by the pipeline stepper),
// cycled by position so admin-added stages beyond the seeded five still differ.
const OPEN_STAGE_PALETTE = ["var(--seq-2)", "var(--seq-4)", "var(--seq-6)", "var(--seq-8)", "var(--seq-3)", "var(--seq-7)"];
const WON_COLOR = "var(--ok)";
const LOST_COLOR = "var(--err)";

/**
 * Resolved color for a stage: the admin-picked color wins; otherwise a
 * sensible default derived from the stage's own kind + position — WON green,
 * LOST red, and each OPEN stage its own step of the ordinal blue ramp — so a
 * board with only default (color-less) stages still reads as distinct columns
 * instead of a wall of identical grey dots. Callers pass the full stage
 * (kind/position optional → neutral fallback).
 */
export function stageColor(stage: { color: string | null; kind?: string; position?: number }): string {
  if (stage.color) return stage.color;
  if (stage.kind === "WON") return WON_COLOR;
  if (stage.kind === "LOST") return LOST_COLOR;
  if (stage.position != null) return OPEN_STAGE_PALETTE[(stage.position - 1) % OPEN_STAGE_PALETTE.length];
  return DEFAULT_STAGE_COLOR;
}

/**
 * Fades a stage color (a `var(--…)` token or a `#rrggbb` hex) into a light
 * chip/column background on the current surface. Falls back to a neutral
 * surface tint for anything else (defensive — admin-entered color is free text).
 */
export function tint(color: string): string {
  if (!/^(var\(--[a-z0-9-]+\)|#[0-9a-f]{6})$/i.test(color)) return "var(--surface-2)";
  return `color-mix(in srgb, ${color} 10%, var(--surface))`;
}

/**
 * True once dueAt has passed for a deal still in an OPEN stage — drives the
 * red due-chip. WON/LOST outcomes never show as overdue: the decision is
 * already made, so a stale reminder shouldn't shout red (same reasoning as
 * TmTasksPage excluding DONE/CANCELLED from its overdue calc).
 */
export function isOverdue(
  offer: { dueAt: string | null; stage: { kind: OfferStage["kind"] } | null },
  now: Date = new Date(),
): boolean {
  if (!offer.dueAt || !offer.stage) return false;
  if (offer.stage.kind !== "OPEN") return false;
  return new Date(offer.dueAt).getTime() < now.getTime();
}

/**
 * First (up to) two initials of a display name, uppercased, for the card's
 * owner avatar — "?" when there's nothing to show. Mirrors the
 * TmTaskDetailPage PersonPill idiom.
 */
export function ownerInitials(name: string | null | undefined): string {
  const display = name?.trim();
  if (!display) return "?";
  return display
    .split(/\s+/)
    .map((s) => s[0])
    .filter(Boolean)
    .slice(0, 2)
    .join("")
    .toUpperCase();
}

/**
 * Short card subtitle: the first line item's product name, with a "+N"
 * suffix when there are more. Empty string (render nothing) for a deal with
 * no items yet — early-pipeline deals are expected to have none.
 */
export function cardSubtitle(offer: { items: Pick<Offer["items"][number], "name">[] }): string {
  if (offer.items.length === 0) return "";
  const first = offer.items[0].name;
  return offer.items.length > 1 ? `${first} +${offer.items.length - 1}` : first;
}
